import React, { useState, useEffect } from 'react';
import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || '/api'
});

// Add axios interceptor to include authentication token
api.interceptors.request.use(
  (config) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.token) {
      config.headers.Authorization = `Bearer ${user.token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const Reports = () => {
  const [salesData, setSalesData] = useState(null);
  const [bookingData, setBookingData] = useState(null);
  const [voucherData, setVoucherData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Helper to format date as YYYY-MM-DD without timezone issues
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const [dateRange, setDateRange] = useState({
    startDate: formatDateLocal(new Date(new Date().getFullYear(), new Date().getMonth(), 1)), // First day of current month
    endDate: formatDateLocal(new Date()) // Today
  });

  const fetchReports = async () => {
    setLoading(true);
    try {
      const [salesResponse, bookingsResponse, voucherResponse] = await Promise.all([
        api.get(`/reports/sales?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`),
        api.get(`/reports/bookings?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`),
        api.get(`/reports/voucher-redemptions?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`)
      ]);
      
      setSalesData(salesResponse.data);
      setBookingData(bookingsResponse.data);
      setVoucherData(voucherResponse.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
      // If API doesn't exist yet, show placeholder data
      setSalesData({
        totalRevenue: 0,
        totalBookings: 0,
        averageBookingValue: 0,
        roomTypeBreakdown: []
      });
      setBookingData({
        totalReservations: 0,
        confirmedReservations: 0,
        cancelledReservations: 0,
        checkedInReservations: 0,
        checkedOutReservations: 0,
        dailyBookings: []
      });
      setVoucherData({
        summary: {
          totalRedemptions: 0,
          uniqueVouchersUsed: 0,
          totalDiscountGiven: 0,
          averageDiscountPerRedemption: 0
        },
        voucherBreakdown: [],
        dailyTrends: [],
        roomTypeUsage: []
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleDateRangeChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGenerateReport = () => {
    fetchReports();
  };

  const handleExportExcel = () => {
    const exportUrl = `/reports/export/excel?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`;
    window.open(api.defaults.baseURL + exportUrl, '_blank');
  };

  const handleExportPDF = () => {
    const exportUrl = `/reports/export/pdf?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`;
    window.open(api.defaults.baseURL + exportUrl, '_blank');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="reports-container">
      <div className="reports-header">
        <h2>Reports & Analytics</h2>
        <div className="export-buttons">
          <button 
            className="export-btn excel-btn"
            onClick={handleExportExcel}
            disabled={loading}
            title="Export to Excel"
          >
            📊 Excel
          </button>
          <button 
            className="export-btn pdf-btn"
            onClick={handleExportPDF}
            disabled={loading}
            title="Export to PDF"
          >
            📄 PDF
          </button>
        </div>
      </div>
      
      {/* Date Range Filter */}
      <div className="date-filter-section">
        <h3>Filter by Date Range</h3>
        <div className="date-inputs">
          <div className="date-input-group">
            <label htmlFor="startDate">Start Date:</label>
            <input
              type="date"
              id="startDate"
              value={dateRange.startDate}
              onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
            />
          </div>
          <div className="date-input-group">
            <label htmlFor="endDate">End Date:</label>
            <input
              type="date"
              id="endDate"
              value={dateRange.endDate}
              onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
            />
          </div>
          <button 
            className="generate-report-btn"
            onClick={handleGenerateReport}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Generating reports...</div>
      ) : (
        <div className="reports-content">
          {/* Sales Report */}
          <div className="report-section">
            <h3>Revenue Summary (Collected)</h3>
            <div className="report-cards">
              <div className="report-card revenue-collected">
                <h4>Total Gross Revenue</h4>
                <p className="metric-value">{formatCurrency(salesData?.totalGrossRevenue || 0)}</p>
              </div>
              <div className="report-card discount-voucher">
                <h4>Voucher Discounts</h4>
                <p className="metric-value">{formatCurrency(salesData?.totalVoucherDiscounts || 0)}</p>
              </div>
              <div className="report-card discount-mandatory">
                <h4>SC/PWD Discounts</h4>
                <p className="metric-value">{formatCurrency(salesData?.totalMandatoryDiscounts || 0)}</p>
                <small>Legacy + Multi-Discount System</small>
              </div>
              {/* <div className="report-card">
                <h4>VAT Exemptions</h4>
                <p className="metric-value">₱0.00</p>
                <small>N/A - Non-VAT Registered</small>
              </div> */}
              <div className="report-card">
                <h4>Total Discounts</h4>
                <p className="metric-value">{formatCurrency(salesData?.totalDiscounts || 0)}</p>
                <small>All discount types combined</small>
              </div>
              {/* <div className="report-card">
                <h4>Total Exemptions</h4>
                <p className="metric-value">{formatCurrency(salesData?.totalExemptions || 0)}</p>
                <small>All exemption types combined</small>
              </div> */}
              <div className="report-card revenue-net">
                <h4>Net Revenue</h4>
                <p className="metric-value">{formatCurrency(salesData?.totalRevenue || 0)}</p>
                <small>After all discounts</small>
              </div>
              <div className="report-card">
                <h4>Total Bookings</h4>
                <p className="metric-value">{salesData?.totalBookings || 0}</p>
              </div>
              <div className="report-card">
                <h4>Total Served Bookings</h4>
                <p className="metric-value">{salesData?.servedBookings || 0}</p>
              </div>
              <div className="report-card">
                <h4>Average Booking Value</h4>
                <p className="metric-value">{formatCurrency(salesData?.averageBookingValue || 0)}</p>
              </div>
              <div className="report-card">
                <h4>SC Discounts Applied</h4>
                <p className="metric-value">{salesData?.seniorDiscountsCount || 0}</p>
                <small>Senior Citizen discounts</small>
              </div>
              <div className="report-card">
                <h4>PWD Discounts Applied</h4>
                <p className="metric-value">{salesData?.pwdDiscountsCount || 0}</p>
                <small>PWD discounts</small>
              </div>
              <div className="report-card">
                <h4>Vouchers Redeemed</h4>
                <p className="metric-value">{salesData?.vouchersRedeemedCount || 0}</p>
              </div>
            </div>

            {/* Potential Sales Summary */}
            <h3>Potential Sales Summary (Pending)</h3>
            <div className="report-cards">
              <div className="report-card revenue-potential">
                <h4>Gross Potential Revenue</h4>
                <p className="metric-value">{formatCurrency(salesData?.grossPotentialRevenue || 0)}</p>
              </div>
              <div className="report-card discount-voucher">
                <h4>Voucher Discounts</h4>
                <p className="metric-value">{formatCurrency(salesData?.potentialVoucherDiscounts || 0)}</p>
              </div>
              <div className="report-card discount-mandatory">
                <h4>SC/PWD Discounts</h4>
                <p className="metric-value">{formatCurrency(salesData?.potentialMandatoryDiscounts || 0)}</p>
                <small>Legacy + Multi-Discount System</small>
              </div>
              {/* <div className="report-card">
                <h4>VAT Exemptions</h4>
                <p className="metric-value">₱0.00</p>
                <small>N/A - Non-VAT Registered</small>
              </div> */}
              <div className="report-card revenue-potential">
                <h4>Potential Net Revenue</h4>
                <p className="metric-value">{formatCurrency(salesData?.potentialRevenue || 0)}</p>
                <small>After all discounts</small>
              </div>
              <div className="report-card">
                <h4>Pending Bookings</h4>
                <p className="metric-value">{salesData?.pendingBookings || 0}</p>
              </div>
              <div className="report-card">
                <h4>SC Discounts Pending</h4>
                <p className="metric-value">{salesData?.pendingSeniorDiscounts || 0}</p>
                <small>Senior Citizen discounts</small>
              </div>
              <div className="report-card">
                <h4>PWD Discounts Pending</h4>
                <p className="metric-value">{salesData?.pendingPwdDiscounts || 0}</p>
                <small>PWD discounts</small>
              </div>
              <div className="report-card">
                <h4>Vouchers Pending</h4>
                <p className="metric-value">{salesData?.pendingVouchersRedeemed || 0}</p>
              </div>
            </div>

            {salesData?.roomTypeBreakdown && salesData.roomTypeBreakdown.length > 0 && (
              <div className="room-type-breakdown">
                <h4>Revenue by Room Type</h4>
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Room Type</th>
                      <th>Bookings</th>
                      <th>Revenue (Collected)</th>
                      <th>Potential Revenue</th>
                      <th>Total Expected</th>
                      <th>Avg. Rate</th>
                      <th>Voucher Discounts</th>
                      <th>SC/PWD Discounts</th>
                      {/* <th>VAT Exemptions (N/A)</th> */}
                      <th>Senior Discounts</th>
                      <th>PWD Discounts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesData.roomTypeBreakdown.map((roomType, index) => (
                      <tr key={index}>
                        <td>{roomType.name}</td>
                        <td>{roomType.bookings}</td>
                        <td>{formatCurrency(roomType.revenue)}</td>
                        <td>{formatCurrency(roomType.potentialRevenue || 0)}</td>
                        <td>{formatCurrency(roomType.totalExpectedRevenue || 0)}</td>
                        <td>{formatCurrency(roomType.averageRate)}</td>
                        <td>{formatCurrency(roomType.totalVoucherDiscounts || 0)}</td>
                        <td>{formatCurrency(roomType.totalMandatoryDiscounts)}</td>
                        {/* <td>₱0.00</td> */}
                        <td>{roomType.seniorDiscounts || 0}</td>
                        <td>{roomType.pwdDiscounts || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Booking Summary Report */}
          <div className="report-section">
            <h3>Booking Summary</h3>
            <div className="report-cards">
              <div className="report-card">
                <h4>Total Reservations</h4>
                <p className="metric-value">{bookingData?.totalReservations || 0}</p>
              </div>
              <div className="report-card">
                <h4>Confirmed</h4>
                <p className="metric-value confirmed">{bookingData?.confirmedReservations || 0}</p>
              </div>
              <div className="report-card">
                <h4>Checked In</h4>
                <p className="metric-value checked-in">{bookingData?.checkedInReservations || 0}</p>
              </div>
              <div className="report-card">
                <h4>Checked Out</h4>
                <p className="metric-value checked-out">{bookingData?.checkedOutReservations || 0}</p>
              </div>
              <div className="report-card">
                <h4>Cancelled</h4>
                <p className="metric-value cancelled">{bookingData?.cancelledReservations || 0}</p>
              </div>
            </div>

            {bookingData?.dailyBookings && bookingData.dailyBookings.length > 0 && (
              <div className="daily-bookings">
                <h4>Daily Booking Trend</h4>
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>New Bookings</th>
                      <th>Check-ins</th>
                      <th>Check-outs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookingData.dailyBookings.map((day, index) => (
                      <tr key={index}>
                        <td>{formatDate(day.date)}</td>
                        <td>{day.newBookings}</td>
                        <td>{day.checkIns}</td>
                        <td>{day.checkOuts}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Voucher Redemption Report */}
          <div className="report-section">
            <h3>Voucher Redemption Report</h3>
            <div className="report-cards">
              <div className="report-card">
                <h4>Total Redemptions</h4>
                <p className="metric-value">{voucherData?.summary?.totalRedemptions || 0}</p>
              </div>
              <div className="report-card">
                <h4>Unique Vouchers Used</h4>
                <p className="metric-value">{voucherData?.summary?.uniqueVouchersUsed || 0}</p>
              </div>
              <div className="report-card">
                <h4>Total Discount Given</h4>
                <p className="metric-value">{formatCurrency(voucherData?.summary?.totalDiscountGiven)}</p>
              </div>
              <div className="report-card">
                <h4>Avg. Discount per Use</h4>
                <p className="metric-value">{formatCurrency(voucherData?.summary?.averageDiscountPerRedemption)}</p>
              </div>
            </div>

            {voucherData?.voucherBreakdown && voucherData.voucherBreakdown.length > 0 && (
              <div className="voucher-breakdown">
                <h4>Voucher Performance</h4>
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Voucher Code</th>
                      <th>Type</th>
                      <th>Description</th>
                      <th>System</th>
                      <th>Redemptions</th>
                      <th>Total Discount</th>
                      <th>Avg. Discount</th>
                      <th>Last Used</th>
                    </tr>
                  </thead>
                  <tbody>
                    {voucherData.voucherBreakdown.map((voucher, index) => (
                      <tr key={index}>
                        <td><strong>{voucher.voucherCode}</strong></td>
                        <td>{voucher.voucherType}</td>
                        <td>{voucher.description}</td>
                        <td>
                          <span className={`system-badge ${voucher.systemType?.toLowerCase()}`}>
                            {voucher.systemType === 'LEGACY' ? 'Legacy' : 'Multi-Discount'}
                          </span>
                        </td>
                        <td>{voucher.redemptionCount}</td>
                        <td>{formatCurrency(voucher.totalDiscount)}</td>
                        <td>{formatCurrency(voucher.averageDiscount)}</td>
                        <td>{formatDate(voucher.lastRedemption)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {voucherData?.roomTypeUsage && voucherData.roomTypeUsage.length > 0 && (
              <div className="voucher-room-usage">
                <h4>Voucher Usage by Room Type</h4>
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Room Type</th>
                      <th>Redemptions</th>
                      <th>Total Discount</th>
                      <th>Avg. Discount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {voucherData.roomTypeUsage.map((usage, index) => (
                      <tr key={index}>
                        <td>{usage.roomType}</td>
                        <td>{usage.redemptions}</td>
                        <td>{formatCurrency(usage.totalDiscount)}</td>
                        <td>{formatCurrency(usage.averageDiscount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {voucherData?.dailyTrends && voucherData.dailyTrends.length > 0 && (
              <div className="voucher-daily-trends">
                <h4>Daily Voucher Redemption Trends</h4>
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Redemptions</th>
                      <th>Total Discount</th>
                      <th>Unique Vouchers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {voucherData.dailyTrends.map((day, index) => (
                      <tr key={index}>
                        <td>{formatDate(day.date)}</td>
                        <td>{day.redemptions}</td>
                        <td>{formatCurrency(day.totalDiscount)}</td>
                        <td>{day.uniqueVouchers}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Quick Stats */}
          <div className="report-section">
            <h3>Quick Statistics</h3>
            <div className="quick-stats">
              <div className="stat-item">
                <span className="stat-label">Date Range:</span>
                <span className="stat-value">{formatDate(dateRange.startDate)} - {formatDate(dateRange.endDate)}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Report Generated:</span>
                <span className="stat-value">{new Date().toLocaleString()}</span>
              </div>
              {salesData && bookingData && (
                <div className="stat-item">
                  <span className="stat-label">Completion Rate:</span>
                  <span className="stat-value">
                    {bookingData.totalReservations > 0 
                      ? `${Math.round((bookingData.checkedOutReservations / bookingData.totalReservations) * 100)}%`
                      : '0%'
                    }
                  </span>
                  <span className="stat-hint" style={{ fontSize: '0.75em', color: '#6c757d', marginLeft: '8px' }}>
                    ({bookingData.checkedOutReservations} of {bookingData.totalReservations} completed)
                  </span>
                </div>
              )}
              {voucherData && voucherData.summary && (
                <div className="stat-item">
                  <span className="stat-label">Voucher Usage Rate:</span>
                  <span className="stat-value">
                    {bookingData?.totalReservations > 0 
                      ? `${Math.round((voucherData.summary.totalRedemptions / bookingData.totalReservations) * 100)}%`
                      : '0%'
                    }
                  </span>
                </div>
              )}
              {salesData && voucherData && voucherData.summary && (
                <div className="stat-item">
                  <span className="stat-label">Discount Impact:</span>
                  <span className="stat-value">
                    {salesData.totalRevenue > 0 
                      ? `${Math.round((voucherData.summary.totalDiscountGiven / (salesData.totalRevenue + voucherData.summary.totalDiscountGiven)) * 100)}%`
                      : '0%'
                    }
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
